/**
 * Workbench Store
 * Manages active playlist editing state, track modifications, and undo/redo history
 */

import { create } from 'zustand';
import type { PlaylistDetail, Track, TrackWithInstanceId } from '../api/types';
import type { TrackMatch } from '../api/services/smart-slots';
import type { FilterCriteria, FilterAction } from '../api/services/workbench';
import { toast } from '../components/ui/Toast';

// Maximum number of undo steps to keep in history
const MAX_HISTORY_SIZE = 50;

/**
 * Smart slot criteria for intelligent track insertion
 */
export interface SlotCriteria {
  bpm?: { min: number; max: number };
  keys?: string[]; // Camelot keys (e.g., ['8A', '8B', '9A'])
  energy?: { min: number; max: number };
  valence?: { min: number; max: number };
  danceability?: { min: number; max: number };
}

/**
 * Smart slot definition with position and matching criteria
 */
export interface SmartSlot {
  id: string;
  position: number; // Index in track list where slot exists
  criteria: SlotCriteria;
  filled?: boolean; // Whether a track has been inserted
  suggestedTrackId?: string | null; // AI-suggested track for this slot
  matches?: TrackMatch[]; // Cached matches from API
}

/**
 * AI slot definition for AI-generated track suggestions
 */
export interface AISlot {
  id: string;
  position: number; // Index in track list where slot exists
  suggestedTracks: Track[]; // AI-suggested tracks for this slot
  promptUsed: string; // The prompt that was used to generate suggestions
  filled: boolean; // Whether a track has been inserted
  suggestedTrackId?: string | null; // Currently selected track from suggestions
}

/**
 * Filter preview metadata
 * Stores information about an active filter preview
 */
export interface FilterPreviewMetadata {
  action: FilterAction;
  criteria: FilterCriteria;
  newPlaylistName?: string;
  matchedTrackIds: string[];
  matchedCount: number;
}

/**
 * Workbench capabilities
 * Determines which editing operations are allowed based on playlist type
 */
export interface WorkbenchCapabilities {
  canReorder: boolean;      // Enable drag & drop reordering
  canAddRemove: boolean;    // Enable add/remove tracks
  canSave: boolean;         // Enable "Save Changes" to Spotify
  canLike: boolean;         // Enable like/unlike functionality
  showLikeButton: boolean;  // Show like button in TrackRow
  isReadOnly: boolean;      // General read-only mode indicator
}

/**
 * Compute capabilities based on playlist type
 * Centralizes all playlist-type-specific behavior flags
 */
function computeCapabilities(playlist: PlaylistDetail | null): WorkbenchCapabilities {
  if (!playlist) {
    return {
      canReorder: false,
      canAddRemove: false,
      canSave: false,
      canLike: false,
      showLikeButton: false,
      isReadOnly: true,
    };
  }

  // Liked Songs - special collection
  if (playlist.id === 'liked-songs') {
    return {
      canReorder: false,      // Spotify doesn't support reordering library
      canAddRemove: false,    // Can only unlike (remove via like button)
      canSave: false,         // No save operation
      canLike: true,          // Primary feature!
      showLikeButton: true,   // Show heart icon on every track
      isReadOnly: false,      // Can unlike tracks
    };
  }

  // Regular playlists - full editing
  return {
    canReorder: true,
    canAddRemove: true,
    canSave: true,
    canLike: true,
    showLikeButton: true,
    isReadOnly: false,
  };
}

/**
 * Workbench state interface
 */
export interface WorkbenchState {
  // Active playlist being edited
  activePlaylist: PlaylistDetail | null;
  setActivePlaylist: (playlist: PlaylistDetail | null) => void;

  // Capabilities based on playlist type
  capabilities: WorkbenchCapabilities;

  // Original tracks (as loaded from playlist)
  originalTracks: TrackWithInstanceId[];

  // Modified tracks (working copy)
  tracks: TrackWithInstanceId[];
  setTracks: (tracks: Track[] | TrackWithInstanceId[]) => void;
  addTrack: (track: Track, position?: number) => void;
  addTracks: (tracks: Track[], position?: number) => void;
  removeTrack: (instanceId: string) => void;
  reorderTracks: (fromIndex: number, toIndex: number) => void;
  restoreOriginalOrder: () => void;
  reverseTracks: () => void;

  // Dirty flag - tracks whether unsaved changes exist
  isDirty: boolean;
  markDirty: () => void;
  markClean: () => void;

  // Check if track order matches original (for smart playback)
  isOrderUnchanged: () => boolean;

  // Undo/redo stack
  history: TrackWithInstanceId[][];
  historyIndex: number;
  undo: () => void;
  redo: () => void;
  canUndo: boolean;
  canRedo: boolean;
  resetHistory: () => void;

  // Smart slots for intelligent track insertion
  smartSlots: SmartSlot[];
  addSmartSlot: (position: number, criteria: SlotCriteria) => void;
  removeSmartSlot: (slotId: string) => void;
  updateSmartSlot: (slotId: string, updates: Partial<SmartSlot>) => void;
  activeSlotId: string | null;
  setActiveSlot: (slotId: string | null) => void;

  // AI slots for AI-generated track suggestions
  aiSlots: AISlot[];
  addAISlot: (position: number, tracks: Track[], prompt: string) => void;
  removeAISlot: (slotId: string) => void;
  updateAISlot: (slotId: string, updates: Partial<AISlot>) => void;
  regenerateAISlot: (slotId: string) => Promise<void>;

  // Filter preview mode
  filterPreviewMode: boolean;
  filterPreviewMetadata: FilterPreviewMetadata | null;
  prePreviewTracks: TrackWithInstanceId[];
  setFilterPreview: (metadata: FilterPreviewMetadata, previewTracks: TrackWithInstanceId[]) => void;
  clearFilterPreview: () => void;

  // Reset entire store
  reset: () => void;
}

/**
 * Generate unique ID for smart slots
 */
function generateSlotId(): string {
  return `slot_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Add state to history and manage history size
 */
function addToHistory(
  currentHistory: TrackWithInstanceId[][],
  currentIndex: number,
  newState: TrackWithInstanceId[]
): { history: TrackWithInstanceId[][]; index: number } {
  // Remove any future states if we're not at the end
  const newHistory = currentHistory.slice(0, currentIndex + 1);

  // Add new state
  newHistory.push([...newState]);

  // Trim history if it exceeds max size
  if (newHistory.length > MAX_HISTORY_SIZE) {
    newHistory.shift();
    return { history: newHistory, index: newHistory.length - 1 };
  }

  return { history: newHistory, index: newHistory.length - 1 };
}

/**
 * Workbench store
 */
export const useWorkbenchStore = create<WorkbenchState>((set, get) => ({
  // Initial state
  activePlaylist: null,
  capabilities: computeCapabilities(null),
  originalTracks: [],
  tracks: [],
  isDirty: false,
  history: [[]],
  historyIndex: 0,
  canUndo: false,
  canRedo: false,
  smartSlots: [],
  activeSlotId: null,
  aiSlots: [],
  filterPreviewMode: false,
  filterPreviewMetadata: null,
  prePreviewTracks: [],

  // Set active playlist and initialize tracks
  setActivePlaylist: (playlist) => {
    const tracks = playlist?.tracks.items.map((pt) => ({
      ...pt.track,
      instanceId: crypto.randomUUID(),
    })) ?? [];
    set({
      activePlaylist: playlist,
      capabilities: computeCapabilities(playlist),
      originalTracks: [...tracks], // Store original order
      tracks,
      isDirty: false,
      history: [tracks],
      historyIndex: 0,
      canUndo: false,
      canRedo: false,
      smartSlots: [],
      activeSlotId: null,
      aiSlots: [],
    });
  },

  // Replace all tracks and update history
  setTracks: (tracks) => {
    // Ensure all tracks have instanceIds
    const tracksWithInstanceIds: TrackWithInstanceId[] = tracks.map((track) =>
      'instanceId' in track
        ? track
        : { ...track, instanceId: crypto.randomUUID() }
    );

    const { history, index } = addToHistory(
      get().history,
      get().historyIndex,
      tracksWithInstanceIds
    );
    set({
      tracks: tracksWithInstanceIds,
      history,
      historyIndex: index,
      canUndo: index > 0,
      canRedo: false,
      isDirty: true,
    });
  },

  // Add track at position (default: end)
  addTrack: (track, position) => {
    const currentTracks = get().tracks;
    const newTracks = [...currentTracks];
    const trackWithInstanceId: TrackWithInstanceId = {
      ...track,
      instanceId: crypto.randomUUID(),
    };

    if (position !== undefined && position >= 0 && position <= newTracks.length) {
      newTracks.splice(position, 0, trackWithInstanceId);
    } else {
      newTracks.push(trackWithInstanceId);
    }

    const { history, index } = addToHistory(
      get().history,
      get().historyIndex,
      newTracks
    );

    // Update SmartSlot positions when inserting track
    // All slots at or after the insertion point need to shift down by 1
    const updatedSlots = get().smartSlots.map((slot) => {
      if (position !== undefined && slot.position >= position && !slot.filled) {
        return { ...slot, position: slot.position + 1 };
      }
      return slot;
    });

    // Update AISlot positions when inserting track
    // All AI slots at or after the insertion point need to shift down by 1
    const updatedAISlots = get().aiSlots.map((slot) => {
      if (position !== undefined && slot.position >= position && !slot.filled) {
        return { ...slot, position: slot.position + 1 };
      }
      return slot;
    });

    set({
      tracks: newTracks,
      history,
      historyIndex: index,
      canUndo: index > 0,
      canRedo: false,
      isDirty: true,
      smartSlots: updatedSlots,
      aiSlots: updatedAISlots,
    });
  },

  // Add multiple tracks at position (default: end)
  addTracks: (tracks, position) => {
    const currentTracks = get().tracks;
    const newTracks = [...currentTracks];
    const tracksWithInstanceIds: TrackWithInstanceId[] = tracks.map((track) => ({
      ...track,
      instanceId: crypto.randomUUID(),
    }));

    if (position !== undefined && position >= 0 && position <= newTracks.length) {
      newTracks.splice(position, 0, ...tracksWithInstanceIds);
    } else {
      newTracks.push(...tracksWithInstanceIds);
    }

    const { history, index } = addToHistory(
      get().history,
      get().historyIndex,
      newTracks
    );

    // Update SmartSlot positions when inserting tracks
    // All slots at or after the insertion point need to shift down by number of tracks inserted
    const insertCount = tracksWithInstanceIds.length;
    const updatedSlots = get().smartSlots.map((slot) => {
      if (position !== undefined && slot.position >= position && !slot.filled) {
        return { ...slot, position: slot.position + insertCount };
      }
      return slot;
    });

    // Update AISlot positions when inserting tracks
    // All AI slots at or after the insertion point need to shift down by number of tracks inserted
    const updatedAISlots = get().aiSlots.map((slot) => {
      if (position !== undefined && slot.position >= position && !slot.filled) {
        return { ...slot, position: slot.position + insertCount };
      }
      return slot;
    });

    set({
      tracks: newTracks,
      history,
      historyIndex: index,
      canUndo: index > 0,
      canRedo: false,
      isDirty: true,
      smartSlots: updatedSlots,
      aiSlots: updatedAISlots,
    });
  },

  // Remove track by instanceId
  removeTrack: (instanceId) => {
    const currentTracks = get().tracks;
    const removedIndex = currentTracks.findIndex((t) => t.instanceId === instanceId);
    const newTracks = currentTracks.filter((t) => t.instanceId !== instanceId);

    const { history, index } = addToHistory(
      get().history,
      get().historyIndex,
      newTracks
    );

    // Update SmartSlot positions when removing track
    // All slots after the removed position need to shift up by 1
    const updatedSlots = get().smartSlots.map((slot) => {
      if (removedIndex >= 0 && slot.position > removedIndex) {
        return { ...slot, position: slot.position - 1 };
      }
      return slot;
    });

    // Update AISlot positions when removing track
    // All AI slots after the removed position need to shift up by 1
    const updatedAISlots = get().aiSlots.map((slot) => {
      if (removedIndex >= 0 && slot.position > removedIndex) {
        return { ...slot, position: slot.position - 1 };
      }
      return slot;
    });

    set({
      tracks: newTracks,
      history,
      historyIndex: index,
      canUndo: index > 0,
      canRedo: false,
      isDirty: true,
      smartSlots: updatedSlots,
      aiSlots: updatedAISlots,
    });
  },

  // Reorder tracks (drag and drop)
  reorderTracks: (fromIndex, toIndex) => {
    const currentTracks = get().tracks;
    const newTracks = [...currentTracks];
    const [moved] = newTracks.splice(fromIndex, 1);
    if (!moved) return; // Safety check
    newTracks.splice(toIndex, 0, moved);

    const { history, index } = addToHistory(
      get().history,
      get().historyIndex,
      newTracks
    );

    // Update SmartSlot positions when reordering tracks
    // This is complex - for now, we'll adjust positions based on the shift
    const updatedSlots = get().smartSlots.map((slot) => {
      const pos = slot.position;

      // If slot is at the source position, move it to destination
      if (pos === fromIndex) {
        return { ...slot, position: toIndex };
      }

      // If moving down (fromIndex < toIndex)
      if (fromIndex < toIndex) {
        // Slots between fromIndex and toIndex shift up by 1
        if (pos > fromIndex && pos <= toIndex) {
          return { ...slot, position: pos - 1 };
        }
      } else {
        // Moving up (fromIndex > toIndex)
        // Slots between toIndex and fromIndex shift down by 1
        if (pos >= toIndex && pos < fromIndex) {
          return { ...slot, position: pos + 1 };
        }
      }

      return slot;
    });

    // Update AISlot positions when reordering tracks
    // Apply the same logic as for SmartSlots
    const updatedAISlots = get().aiSlots.map((slot) => {
      const pos = slot.position;

      // If slot is at the source position, move it to destination
      if (pos === fromIndex) {
        return { ...slot, position: toIndex };
      }

      // If moving down (fromIndex < toIndex)
      if (fromIndex < toIndex) {
        // Slots between fromIndex and toIndex shift up by 1
        if (pos > fromIndex && pos <= toIndex) {
          return { ...slot, position: pos - 1 };
        }
      } else {
        // Moving up (fromIndex > toIndex)
        // Slots between toIndex and fromIndex shift down by 1
        if (pos >= toIndex && pos < fromIndex) {
          return { ...slot, position: pos + 1 };
        }
      }

      return slot;
    });

    set({
      tracks: newTracks,
      history,
      historyIndex: index,
      canUndo: index > 0,
      canRedo: false,
      isDirty: true,
      smartSlots: updatedSlots,
      aiSlots: updatedAISlots,
    });
  },

  // Restore original track order (as loaded from playlist)
  restoreOriginalOrder: () => {
    const originalTracks = get().originalTracks;

    // Safety check - don't restore if no original tracks exist
    if (!originalTracks || originalTracks.length === 0) {
      console.warn('restoreOriginalOrder: No original tracks to restore');
      return;
    }

    const restoredTracks = [...originalTracks];

    const { history, index } = addToHistory(
      get().history,
      get().historyIndex,
      restoredTracks
    );

    set({
      tracks: restoredTracks,
      history,
      historyIndex: index,
      canUndo: index > 0,
      canRedo: false,
      isDirty: false, // Restoring original = clean state
    });
  },

  // Reverse current track order
  reverseTracks: () => {
    const currentTracks = get().tracks;
    const reversedTracks = [...currentTracks].reverse();

    const { history, index } = addToHistory(
      get().history,
      get().historyIndex,
      reversedTracks
    );

    set({
      tracks: reversedTracks,
      history,
      historyIndex: index,
      canUndo: index > 0,
      canRedo: false,
      isDirty: true,
    });
  },

  // Dirty flag management
  markDirty: () => set({ isDirty: true }),
  markClean: () => set({ isDirty: false }),

  // Check if track order matches original (for smart contextUri playback)
  isOrderUnchanged: () => {
    const { tracks, originalTracks } = get();
    if (tracks.length !== originalTracks.length) return false;
    return tracks.every((track, index) => track.id === originalTracks[index]?.id);
  },

  // Undo last change
  undo: () => {
    const { history, historyIndex } = get();
    if (historyIndex > 0) {
      const newIndex = historyIndex - 1;
      const prevState = history[newIndex];
      if (!prevState) return; // Safety check
      set({
        tracks: [...prevState],
        historyIndex: newIndex,
        canUndo: newIndex > 0,
        canRedo: true,
        isDirty: true,
      });
    }
  },

  // Redo last undone change
  redo: () => {
    const { history, historyIndex } = get();
    if (historyIndex < history.length - 1) {
      const newIndex = historyIndex + 1;
      const nextState = history[newIndex];
      if (!nextState) return; // Safety check
      set({
        tracks: [...nextState],
        historyIndex: newIndex,
        canUndo: true,
        canRedo: newIndex < history.length - 1,
        isDirty: true,
      });
    }
  },

  // Reset history to current state
  resetHistory: () => {
    const currentTracks = get().tracks;
    set({
      history: [currentTracks],
      historyIndex: 0,
      canUndo: false,
      canRedo: false,
    });
  },

  // Add smart slot at position
  addSmartSlot: (position, criteria) => {
    const newSlot: SmartSlot = {
      id: generateSlotId(),
      position,
      criteria,
      filled: false,
      suggestedTrackId: null,
    };
    set({
      smartSlots: [...get().smartSlots, newSlot],
      activeSlotId: newSlot.id,
    });
  },

  // Remove smart slot by ID
  removeSmartSlot: (slotId) => {
    set({
      smartSlots: get().smartSlots.filter((slot) => slot.id !== slotId),
      activeSlotId: get().activeSlotId === slotId ? null : get().activeSlotId,
    });
  },

  // Update smart slot properties
  updateSmartSlot: (slotId, updates) => {
    set({
      smartSlots: get().smartSlots.map((slot) =>
        slot.id === slotId ? { ...slot, ...updates } : slot
      ),
    });
  },

  // Set active smart slot
  setActiveSlot: (slotId) => set({ activeSlotId: slotId }),

  // Add AI slot at position
  addAISlot: (position, tracks, prompt) => {
    const newSlot: AISlot = {
      id: generateSlotId(),
      position,
      suggestedTracks: tracks,
      promptUsed: prompt,
      filled: false,
      suggestedTrackId: null,
    };
    set({
      aiSlots: [...get().aiSlots, newSlot],
    });
  },

  // Remove AI slot by ID
  removeAISlot: (slotId) => {
    set({
      aiSlots: get().aiSlots.filter((slot) => slot.id !== slotId),
    });
  },

  // Update AI slot properties
  updateAISlot: (slotId, updates) => {
    set({
      aiSlots: get().aiSlots.map((slot) =>
        slot.id === slotId ? { ...slot, ...updates } : slot
      ),
    });
  },

  // Regenerate AI slot suggestions
  regenerateAISlot: async (slotId) => {
    const slot = get().aiSlots.find((s) => s.id === slotId);
    const activePlaylist = get().activePlaylist;

    if (!slot || !activePlaylist) {
      console.warn('regenerateAISlot: Slot or active playlist not found');
      return;
    }

    try {
      // Import askAI service dynamically to avoid circular dependencies
      const { askAIService } = await import('../api/services/askAI');

      const response = await askAIService.generateTracks({
        playlistId: activePlaylist.id,
        position: slot.position,
        trackCount: slot.suggestedTracks.length,
        promptType: 'auto',
      });

      get().updateAISlot(slotId, {
        suggestedTracks: response.tracks,
        promptUsed: response.promptUsed,
      });

      toast.success('AI suggestions regenerated');
    } catch (error) {
      console.error('Failed to regenerate AI slot:', error);
      toast.error('Failed to regenerate suggestions');
      throw error;
    }
  },

  // Set filter preview mode
  setFilterPreview: (metadata, previewTracks) => {
    set({
      filterPreviewMode: true,
      filterPreviewMetadata: metadata,
      prePreviewTracks: [...get().tracks], // Snapshot current state
      tracks: previewTracks, // Apply preview
      isDirty: true,
    });
  },

  // Clear filter preview and restore previous state
  clearFilterPreview: () => {
    const { prePreviewTracks } = get();
    if (prePreviewTracks.length > 0) {
      set({
        filterPreviewMode: false,
        filterPreviewMetadata: null,
        tracks: [...prePreviewTracks], // Restore previous state
        prePreviewTracks: [],
      });
    } else {
      // Just clear preview mode if no snapshot exists
      set({
        filterPreviewMode: false,
        filterPreviewMetadata: null,
        prePreviewTracks: [],
      });
    }
  },

  // Reset entire store
  reset: () =>
    set({
      activePlaylist: null,
      capabilities: computeCapabilities(null),
      originalTracks: [],
      tracks: [],
      isDirty: false,
      history: [[]],
      historyIndex: 0,
      canUndo: false,
      canRedo: false,
      smartSlots: [],
      activeSlotId: null,
      aiSlots: [],
      filterPreviewMode: false,
      filterPreviewMetadata: null,
      prePreviewTracks: [],
    }),
}));
